import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import type { Point2D } from '@shared/models/geometry.model';
import { getDistance } from '@shared/helpers/geometry/coordinates.helpers';

import {
  blueprintPageUVToMetric,
  metricPointToBlueprintPageUV,
  type BlueprintPageUV,
} from '@shared/helpers/design/blueprint-underlay.helpers';

/** Cmd on macOS / Ctrl on Windows bypass tracing snaps; Shift keeps them enabled. */
export const isBlueprintDrawingSnapDisabled = (
  event: Pick<MouseEvent, 'metaKey' | 'ctrlKey' | 'shiftKey'>,
  isMac: boolean,
): boolean => (isMac ? event.metaKey : event.ctrlKey);

/** Ink sample in page space: origin at page center, ±0.5 at the edges. */
export type BlueprintInkPagePoint = BlueprintPageUV;

/**
 * Spatial index of dark line samples from a blueprint raster, in page UV space
 * so placement/scale/rotation changes do not require re-scanning pixels.
 */
export type BlueprintInkSnapIndex = {
  points: BlueprintInkPagePoint[];
  /** UV cell key → indices into {@link points}. */
  cells: Map<string, number[]>;
  cellSize: number;
};

type ExtractBlueprintInkOptions = {
  /** Max luminance (0–255) treated as ink. */
  inkLuminanceMax?: number;
  /** Cap on stored samples after thinning. */
  maxPoints?: number;
};

const DEFAULT_INK_LUMINANCE_MAX = 170;
const DEFAULT_MAX_POINTS = 40_000;
const DEFAULT_UV_CELL_SIZE = 1 / 64;

export { blueprintPageUVToMetric, metricPointToBlueprintPageUV };

type UnderlayFrame = Pick<
  BlueprintUnderlay,
  'center' | 'widthMeters' | 'heightMeters' | 'rotationDeg'
>;

/**
 * Extract ink/edge samples from raster {@link ImageData} into a UV snap index.
 * Prefers dark pixels that border lighter paper so filled regions are ignored.
 */
export const extractBlueprintInkSnapIndex = (
  imageData: ImageData,
  options?: ExtractBlueprintInkOptions,
): BlueprintInkSnapIndex => {
  const inkLuminanceMax = options?.inkLuminanceMax ?? DEFAULT_INK_LUMINANCE_MAX;
  const maxPoints = options?.maxPoints ?? DEFAULT_MAX_POINTS;
  const { width, height, data } = imageData;
  const points: BlueprintInkPagePoint[] = [];

  if (!(width > 1) || !(height > 1)) {
    return { points, cells: new Map(), cellSize: DEFAULT_UV_CELL_SIZE };
  }

  const luminanceAt = (x: number, y: number): number => {
    const i = (y * width + x) * 4;
    const r = data[i] ?? 255;
    const g = data[i + 1] ?? 255;
    const b = data[i + 2] ?? 255;
    const a = data[i + 3] ?? 255;
    if (a < 32) return 255;
    return 0.299 * r + 0.587 * g + 0.114 * b;
  };

  // Scan interior pixels; keep dark samples that touch lighter paper (line edges).
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const lum = luminanceAt(x, y);
      if (lum > inkLuminanceMax) continue;

      const hasPaperNeighbor =
        luminanceAt(x - 1, y) > inkLuminanceMax + 25 ||
        luminanceAt(x + 1, y) > inkLuminanceMax + 25 ||
        luminanceAt(x, y - 1) > inkLuminanceMax + 25 ||
        luminanceAt(x, y + 1) > inkLuminanceMax + 25;
      if (!hasPaperNeighbor) continue;

      points.push({
        u: x / (width - 1) - 0.5,
        v: y / (height - 1) - 0.5,
      });
    }
  }

  const thinned =
    points.length <= maxPoints ? points : thinPointsEvenly(points, maxPoints);

  return buildInkSnapIndex(thinned, DEFAULT_UV_CELL_SIZE);
};

/** Minimum linearity (0–1) before projecting onto a local ink ridge. */
const SOFT_LINE_LINEARITY_MIN = 0.72;
/** Local neighborhood around the nearest ink sample, as a fraction of snap radius. */
const SOFT_LINE_NEIGHBORHOOD_SCALE = 1.75;

/**
 * Soft-snap a local-meter pointer onto blueprint ink lines within range.
 * Projects onto the local ink ridge so wall dots slide along strokes instead of
 * latching only to discrete sample points.
 */
export const snapMetricPointToBlueprintInk = (
  underlay: UnderlayFrame,
  index: BlueprintInkSnapIndex,
  point: Point2D,
  maxDistanceMeters: number,
): { point: Point2D; snapped: boolean } => {
  if (!index.points.length || !(maxDistanceMeters > 0)) {
    return { point, snapped: false };
  }

  const page = metricPointToBlueprintPageUV(underlay, point);
  if (!(underlay.widthMeters > 0) || !(underlay.heightMeters > 0)) {
    return { point, snapped: false };
  }
  // Axis-aware UV radius so wide pages do not search many meters along U.
  const maxUu = maxDistanceMeters / underlay.widthMeters;
  const maxUv = maxDistanceMeters / underlay.heightMeters;
  const nearby = collectInkSamplesNear(index, page, maxUu, maxUv);
  if (!nearby.length) return { point, snapped: false };

  let nearest = nearby[0]!;
  for (const sample of nearby) {
    if (sample.distSq < nearest.distSq) nearest = sample;
  }

  const neighborhoodUu = maxUu * SOFT_LINE_NEIGHBORHOOD_SCALE;
  const neighborhoodUv = maxUv * SOFT_LINE_NEIGHBORHOOD_SCALE;
  const local: BlueprintInkPagePoint[] = [];
  for (const sample of nearby) {
    const du = sample.point.u - nearest.point.u;
    const dv = sample.point.v - nearest.point.v;
    if (
      (du * du) / (neighborhoodUu * neighborhoodUu + 1e-12) +
        (dv * dv) / (neighborhoodUv * neighborhoodUv + 1e-12) <=
      1
    ) {
      local.push(sample.point);
    }
  }

  const softPage = projectOntoLocalInkLine(page, local) ?? nearest.point;
  // Never soft-snap outside the page frame (was jumping dots off the image).
  if (Math.abs(softPage.u) > 0.5 + 1e-6 || Math.abs(softPage.v) > 0.5 + 1e-6) {
    return { point, snapped: false };
  }
  const snapped = blueprintPageUVToMetric(underlay, softPage);
  // Final check in meters (UV was an approximation when the page is not square).
  if (getDistance(point, snapped) > maxDistanceMeters) {
    return { point, snapped: false };
  }
  return { point: snapped, snapped: true };
};

type InkSampleHit = {
  point: BlueprintInkPagePoint;
  distSq: number;
};

/**
 * Collect ink samples within an elliptical UV radius of {@link page}.
 */
const collectInkSamplesNear = (
  index: BlueprintInkSnapIndex,
  page: BlueprintInkPagePoint,
  maxUu: number,
  maxUv: number,
): InkSampleHit[] => {
  const cellRadius = Math.max(
    1,
    Math.ceil(Math.max(maxUu, maxUv) / index.cellSize) + 1,
  );
  const cu = Math.floor(page.u / index.cellSize);
  const cv = Math.floor(page.v / index.cellSize);
  const hits: InkSampleHit[] = [];
  const uuScale = maxUu * maxUu + 1e-12;
  const vvScale = maxUv * maxUv + 1e-12;

  for (let dv = -cellRadius; dv <= cellRadius; dv++) {
    for (let du = -cellRadius; du <= cellRadius; du++) {
      const bucket = index.cells.get(cellKey(cu + du, cv + dv));
      if (!bucket) continue;
      for (const idx of bucket) {
        const sample = index.points[idx];
        if (!sample) continue;
        const duv = sample.u - page.u;
        const dvv = sample.v - page.v;
        // Ellipse in UV so meters match on both axes of a non-square page.
        if ((duv * duv) / uuScale + (dvv * dvv) / vvScale > 1) continue;
        hits.push({ point: sample, distSq: duv * duv + dvv * dvv });
      }
    }
  }
  return hits;
};

/**
 * Project {@link page} onto the dominant local ink direction when samples form a line.
 * Returns undefined at corners/blobs so the caller can fall back to the nearest sample.
 */
const projectOntoLocalInkLine = (
  page: BlueprintInkPagePoint,
  local: BlueprintInkPagePoint[],
): BlueprintInkPagePoint | undefined => {
  if (local.length < 3) return undefined;

  let meanU = 0;
  let meanV = 0;
  for (const sample of local) {
    meanU += sample.u;
    meanV += sample.v;
  }
  meanU /= local.length;
  meanV /= local.length;

  let suu = 0;
  let svv = 0;
  let suv = 0;
  for (const sample of local) {
    const du = sample.u - meanU;
    const dv = sample.v - meanV;
    suu += du * du;
    svv += dv * dv;
    suv += du * dv;
  }

  // Principal axis of the local covariance (2×2 symmetric eigen).
  const trace = suu + svv;
  const det = suu * svv - suv * suv;
  const disc = Math.max(0, trace * trace * 0.25 - det);
  const lambda1 = trace * 0.5 + Math.sqrt(disc);
  const lambda2 = trace * 0.5 - Math.sqrt(disc);
  const total = lambda1 + lambda2;
  if (!(total > 1e-12)) return undefined;
  if (lambda1 / total < SOFT_LINE_LINEARITY_MIN) return undefined;

  let dirU = 1;
  let dirV = 0;
  if (Math.abs(suv) > 1e-12 || Math.abs(suu - lambda1) > 1e-12) {
    dirU = lambda1 - svv;
    dirV = suv;
    // Fallback when the matrix is nearly diagonal along V.
    if (Math.abs(dirU) < 1e-12 && Math.abs(dirV) < 1e-12) {
      dirU = suv;
      dirV = lambda1 - suu;
    }
  } else if (svv > suu) {
    dirU = 0;
    dirV = 1;
  }
  const dirLen = Math.hypot(dirU, dirV);
  if (!(dirLen > 1e-12)) return undefined;
  dirU /= dirLen;
  dirV /= dirLen;

  const toPageU = page.u - meanU;
  const toPageV = page.v - meanV;
  let t = toPageU * dirU + toPageV * dirV;

  // Clamp onto the span of local samples so we stay on the stroke.
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const sample of local) {
    const tSample = (sample.u - meanU) * dirU + (sample.v - meanV) * dirV;
    if (tSample < tMin) tMin = tSample;
    if (tSample > tMax) tMax = tSample;
  }
  if (Number.isFinite(tMin) && Number.isFinite(tMax)) {
    t = Math.max(tMin, Math.min(tMax, t));
  }

  return {
    u: meanU + dirU * t,
    v: meanV + dirV * t,
  };
};

/**
 * Rasterize a data-URL image to {@link ImageData}, downscaling long side to
 * {@link maxDimension} for extract performance.
 */
export const loadBlueprintImageData = async (
  imageDataUrl: string,
  maxDimension = 1280,
): Promise<ImageData | undefined> => {
  if (typeof document === 'undefined') return undefined;

  const image = await loadHtmlImage(imageDataUrl);
  if (!image.naturalWidth || !image.naturalHeight) return undefined;

  const scale = Math.min(
    1,
    maxDimension / Math.max(image.naturalWidth, image.naturalHeight),
  );
  const width = Math.max(2, Math.round(image.naturalWidth * scale));
  const height = Math.max(2, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return undefined;
  ctx.drawImage(image, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height);
};

/**
 * Build a UV spatial hash for ink samples.
 */
export const buildInkSnapIndex = (
  points: BlueprintInkPagePoint[],
  cellSize: number = DEFAULT_UV_CELL_SIZE,
): BlueprintInkSnapIndex => {
  const cells = new Map<string, number[]>();
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const key = cellKey(Math.floor(p.u / cellSize), Math.floor(p.v / cellSize));
    const bucket = cells.get(key);
    if (bucket) bucket.push(i);
    else cells.set(key, [i]);
  }
  return { points, cells, cellSize };
};

const cellKey = (u: number, v: number): string => `${u}:${v}`;

/** Keep a stride-sampled subset so dense scans stay interactive. */
const thinPointsEvenly = (
  points: BlueprintInkPagePoint[],
  maxPoints: number,
): BlueprintInkPagePoint[] => {
  if (points.length <= maxPoints) return points;
  const stride = points.length / maxPoints;
  const out: BlueprintInkPagePoint[] = [];
  for (let i = 0; i < maxPoints; i++) {
    const sample = points[Math.floor(i * stride)];
    if (sample) out.push(sample);
  }
  return out;
};

const loadHtmlImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error('Failed to decode blueprint image for snap index'));
    image.src = src;
  });
